import { Box, Container, Typography, Divider, Skeleton } from '@mui/material';
import { fetchTermsOfService, fetchPrivacyPolicy } from '../api/services';
import { useFetch } from '../hooks';
import { ErrorDisplay, MarkdownRenderer } from '../components/common';
import { DESIGN_TOKENS } from '../styles/theme';

// ============================================================
// CONTENT PAGE LAYOUT (shared for Terms & Privacy)
// ============================================================

interface ContentPageLayoutProps {
  pageTitle: string;
  lastUpdatedText?: string;
  markdownContent: string;
}

function ContentPageLayout({
  pageTitle,
  lastUpdatedText,
  markdownContent,
}: ContentPageLayoutProps) {
  return (
    <Box sx={{ backgroundColor: DESIGN_TOKENS.colors.background, minHeight: '60vh' }}>
      {/* Header */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${DESIGN_TOKENS.colors.primaryLighter}, ${DESIGN_TOKENS.colors.surface})`,
          borderBottom: `1px solid ${DESIGN_TOKENS.colors.border}`,
          py: 7,
        }}
      >
        <Container maxWidth="md">
          <Typography variant="h1" sx={{ fontSize: { xs: '2rem', md: '2.75rem' } }}>
            {pageTitle}
          </Typography>
          {lastUpdatedText && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {lastUpdatedText}
            </Typography>
          )}
        </Container>
      </Box>

      {/* Content */}
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Box
          sx={{
            backgroundColor: DESIGN_TOKENS.colors.surface,
            borderRadius: DESIGN_TOKENS.borderRadius.card,
            border: `1px solid ${DESIGN_TOKENS.colors.border}`,
            p: { xs: 3, md: 5 },
          }}
        >
          <MarkdownRenderer content={markdownContent} />
        </Box>
        <Divider sx={{ my: 4 }} />
        <Typography variant="body2" color="text.secondary" align="center">
          W razie pytań skontaktuj się z nami:{' '}
          <a href="mailto:kontakt@qproconnect.com" style={{ color: DESIGN_TOKENS.colors.primary }}>
            kontakt@qproconnect.com
          </a>
        </Typography>
      </Container>
    </Box>
  );
}

// ============================================================
// LOADING SKELETON FOR CONTENT PAGES
// ============================================================

function ContentPageSkeleton() {
  return (
    <Container maxWidth="md" sx={{ py: 6 }}>
      <Skeleton variant="text" height={60} width="60%" sx={{ mb: 2 }} />
      <Skeleton variant="text" height={24} width="30%" sx={{ mb: 4 }} />
      {Array.from({ length: 8 }).map((_, skeletonIndex) => (
        <Skeleton
          key={skeletonIndex}
          variant="text"
          height={20}
          width={skeletonIndex % 3 === 2 ? '75%' : '100%'}
          sx={{ mb: 1 }}
        />
      ))}
    </Container>
  );
}

// ============================================================
// TERMS OF SERVICE PAGE
// ============================================================

export function TermsOfServicePage() {
  const { data: termsData, isLoading, error } = useFetch(fetchTermsOfService);

  if (isLoading) return <ContentPageSkeleton />;

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <ErrorDisplay message={error} />
      </Container>
    );
  }

  return (
    <ContentPageLayout
      pageTitle={termsData?.data?.title ?? 'Regulamin serwisu'}
      markdownContent={termsData?.data?.content ?? ''}
    />
  );
}

// ============================================================
// PRIVACY POLICY PAGE
// ============================================================

export function PrivacyPolicyPage() {
  const { data: privacyData, isLoading, error } = useFetch(fetchPrivacyPolicy);

  if (isLoading) return <ContentPageSkeleton />;

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <ErrorDisplay message={error} />
      </Container>
    );
  }

  return (
    <ContentPageLayout
      pageTitle={privacyData?.data?.title ?? 'Polityka prywatności'}
      markdownContent={privacyData?.data?.content ?? ''}
    />
  );
}
